import { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import {
  Card,
  HStack,
  Pressable,
  Select,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

type Props = {
  // ISO yyyy-mm-dd. Empty falls back to today.
  value: string;
  onChange: (value: string) => void;
  // ISO yyyy-mm-dd. Days after this are disabled.
  maxDate?: string;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Local-only date helpers. The picker never goes through UTC / toISOString, so
// the day the user clicks is exactly the yyyy-mm-dd we emit, with no timezone
// drift.
const pad = (n: number) => String(n).padStart(2, '0');

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const isoToDate = (iso: string): Date | null => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const dateToISO = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const DateField = ({ value, onChange, maxDate = todayISO() }: Props) => {
  // Commit today the first time we render without a selection, so the calendar
  // always opens on a concrete date.
  useEffect(() => {
    if (!value) onChange(todayISO());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveIso = value || todayISO();
  const selected = isoToDate(effectiveIso);
  const base = selected ?? new Date();

  const [viewYear, setViewYear] = useState(base.getFullYear());
  const [viewMonth, setViewMonth] = useState(base.getMonth());

  const maxObj = isoToDate(maxDate);
  const maxYear = maxObj ? maxObj.getFullYear() : new Date().getFullYear();
  const years: number[] = [];
  for (let y = maxYear; y >= maxYear - 30; y--) years.push(y);

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <VStack align={'stretch'} gap={2}>
      {/* Current selection */}
      <HStack
        justify={'space-between'}
        align={'center'}
        borderWidth={1}
        borderColor={'cloud'}
        rounded={'md'}
        px={3}
        py={2}
        bg={'white'}
      >
        <Text fontSize={'sm'} color={'evergreen'}>
          {format(selected ?? new Date(), 'MMM d, yyyy')}
        </Text>
        <Box as={'span'} color={'aqua'} display={'inline-flex'}>
          <CalendarIcon size={16} />
        </Box>
      </HStack>

      {/* Always-expanded calendar */}
      <Card borderWidth={1} borderColor={'cloud'} bg={'canvas'} p={3}>
        <HStack gap={2} mb={3} align={'center'}>
          <Box flex={1}>
            <Select
              selectedValue={String(viewMonth)}
              onValueChange={(v) => setViewMonth(Number(v))}
            >
              {MONTHS.map((m, i) => (
                <Select.Item key={m} value={String(i)} label={m} />
              ))}
            </Select>
          </Box>
          <Box w={'110px'}>
            <Select
              selectedValue={String(viewYear)}
              onValueChange={(v) => setViewYear(Number(v))}
            >
              {years.map((y) => (
                <Select.Item key={y} value={String(y)} label={String(y)} />
              ))}
            </Select>
          </Box>
        </HStack>

        {/* Weekday header */}
        <Box
          display={'grid'}
          gridTemplateColumns={'repeat(7, 1fr)'}
          gap={1}
          mb={1}
        >
          {WEEKDAYS.map((d) => (
            <Text
              key={d}
              textAlign={'center'}
              fontSize={'xs'}
              fontWeight={600}
              color={'bluegrey'}
            >
              {d}
            </Text>
          ))}
        </Box>

        {/* Day grid */}
        <Box display={'grid'} gridTemplateColumns={'repeat(7, 1fr)'} gap={1}>
          {cells.map((d, i) => {
            if (d === null) return <Box key={`empty-${i}`} />;

            const dayDate = new Date(viewYear, viewMonth, d);
            const disabled = maxObj ? dayDate > maxObj : false;
            const isSelected =
              !!selected &&
              selected.getFullYear() === viewYear &&
              selected.getMonth() === viewMonth &&
              selected.getDate() === d;

            return (
              <Pressable
                key={d}
                disabled={disabled}
                onClick={() => onChange(dateToISO(dayDate))}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                boxSize={8}
                mx={'auto'}
                rounded={'full'}
                fontSize={'sm'}
                bg={isSelected ? 'seafoam' : 'transparent'}
                color={disabled ? 'steel' : isSelected ? 'canvas' : 'evergreen'}
                cursor={disabled ? 'not-allowed' : 'pointer'}
                _hover={disabled || isSelected ? undefined : { bg: 'feather' }}
              >
                {d}
              </Pressable>
            );
          })}
        </Box>
      </Card>
    </VStack>
  );
};

export default DateField;
